/* eslint-disable no-extend-native, no-console -- Intentionally patching DOM primitives to survive third-party page translators; dev-only diagnostics use console. */

/**
 * Guards against crashes caused by in-browser page translators (Google Translate,
 * Yandex, etc.) that wrap text nodes in <font> elements and re-parent them.
 *
 * React keeps a reference to the original text node and, on a later structural
 * update, calls removeChild/insertBefore on the parent it expects — which is no
 * longer the node's actual parent — throwing
 * "NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be
 * removed is not a child of this node." and taking down the whole app via the
 * router errorElement.
 *
 * We make these two DOM primitives tolerant: if the node is not where React
 * thinks it is, we no-op (or fall back to appendChild) instead of throwing.
 * Must run once, before the first React render.
 *
 * Trade-off: this also silences legitimate DOM errors in our own code, so in dev
 * we log a warning when the guard fires. If a warning appears with no page
 * translator active, treat it as a real bug to investigate, not translator noise.
 *
 * Ref: https://github.com/facebook/react/issues/11538
 */
export function installBrowserTranslateGuard(): void {
	if (typeof Node !== "function" || !Node.prototype) {
		return;
	}

	const isDev = import.meta.env.DEV;

	const originalRemoveChild = Node.prototype.removeChild;
	Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
		if (child.parentNode !== this) {
			if (isDev) {
				console.warn(
					"[browserTranslateGuard] Suppressed removeChild on a node that is not a child of the target. " +
						"Usually a page translator; if none is active, this may hide a real DOM bug.",
					{ child, expectedParent: this, actualParent: child.parentNode },
				);
			}
			return child;
		}
		return originalRemoveChild.call(this, child) as T;
	};

	const originalInsertBefore = Node.prototype.insertBefore;
	Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
		if (referenceNode && referenceNode.parentNode !== this) {
			if (isDev) {
				console.warn(
					"[browserTranslateGuard] insertBefore reference is not a child of the target; appending instead. " +
						"Usually a page translator; if none is active, this may hide a real DOM bug.",
					{ newNode, referenceNode, expectedParent: this, actualParent: referenceNode.parentNode },
				);
			}
			return this.appendChild(newNode) as T;
		}
		return originalInsertBefore.call(this, newNode, referenceNode) as T;
	};
}
